import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ChevronRightIcon, MapPinIcon, ShieldAlertIcon, SparklesIcon } from 'lucide-react';
import { alertPlaces, avisQuestions, channelPrefs, topicPrefs } from '../data/avis';
function Toggle({
  label,
  defaultOn
}: {
  label: string;
  defaultOn: boolean;
}) {
  const [on, setOn] = useState(defaultOn);
  return <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => setOn(v => !v)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 ${on ? 'bg-ville-indigo' : 'bg-slate-300'}`} data-id="element-2170" data-mp="33hs16dw5nz9q">
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-[left] duration-150 ease-ville ${on ? 'left-[22px]' : 'left-0.5'}`} data-id="element-2171" data-mp="2zgyd9kipqkgu" />
    </button>;
}
export function ParametresAvis() {
  const [question, setQuestion] = useState('');
  return <main className="bg-white" data-id="element-2172" data-mp="804ehukj1g82">
      <div className="mx-auto max-w-[860px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-2173" data-mp="7zcu7fwdr4x3">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-2174" data-mp="3y9f4vm5pcwx">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-2175" data-mp="hpe7dcn732x8">
            Home
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2176" data-mp="cse8blorj0fm" />
          <Link to="/notifications" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-2177" data-mp="3l8hajbx3vwwk">
            Notifications
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2178" data-mp="3h873rx1hp2gn" />
          <span aria-current="page" data-id="element-2179" data-mp="3v76qv09ct9ap">Settings</span>
        </nav>

        <header className="mt-4" data-id="element-2180" data-mp="3q69a8s3zsnbb">
          <h1 className="text-4xl font-extrabold tracking-tight text-ville-navy lg:text-5xl" data-id="element-2181" data-mp="3q4xya50e8994">
            Notification preferences
          </h1>
          <p className="mt-2 max-w-2xl text-[17px] text-slate-600" data-id="element-2182" data-mp="15gjvef3ah1im">
            Choose the information you want to receive, and how the Town can reach you.
          </p>
        </header>

        <section aria-labelledby="channels-title" className="mt-10" data-id="element-2183" data-mp="1hikx06wbv8bm">
          <h2 id="channels-title" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2184" data-mp="1hgrpkpdw4k2k">
            How would you like to be notified?
          </h2>
          <ul className="mt-4 space-y-2" data-id="element-2185" data-mp="1rh0ls37vu5hy">
            {channelPrefs.map(channel => <li key={channel.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 px-5 py-4" data-id="element-2186" data-mp="tta3nahb4g7i">
                <span className="min-w-0 flex-1" data-id="element-2187" data-mp="yrbmpveqgfiz">
                  <span className="block font-bold text-ville-navy" data-id="element-2188" data-mp="yq0jbwp799se">{channel.label}</span>
                  <span className="block text-sm text-slate-500" data-id="element-2189" data-mp="13pvbe3kpo5f1">{channel.destination}</span>
                </span>
                <Toggle label={channel.label} defaultOn={channel.enabled} data-id="element-2190" data-mp="18jpqetpdfw7s" />
              </li>)}
          </ul>
          <Link to="/my-profile" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline" data-id="element-2191" data-mp="b0o8yti4yvoi">
            Change my contact details
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2192" data-mp="8hb6b79pp2ip" />
          </Link>
        </section>

        <section aria-labelledby="essential-title" className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-id="element-2193" data-mp="3jrknmaopngq">
          <h2 id="essential-title" className="flex items-center gap-2 text-[15px] font-extrabold text-ville-navy" data-id="element-2194" data-mp="3pq7z3r2yckst">
            <ShieldAlertIcon className="h-5 w-5 text-red-600" aria-hidden="true" data-id="element-2195" data-mp="3lp4ih2d2ykqj" />
            Safety and emergency alerts
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-2196" data-mp="3gx4f0zxsx9p7">
            These messages may be sent when they are necessary for your safety or the safety of the community. They cannot be turned off completely.
          </p>
          <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-600" data-id="element-2197" data-mp="36xvko5z0xt2b">
            <CheckIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-2198" data-mp="2xyib7dawh4hl" />
            Always on
          </p>
        </section>

        <section aria-labelledby="interests-title" className="mt-10" data-id="element-2199" data-mp="3mqzeyiq0cris">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-2200" data-mp="3rp0uwus50aq1">My preferences</p>
          <h2 id="interests-title" className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2201" data-mp="3dy5um6vpf1y2">
            What interests you
          </h2>
          <ul className="mt-4 space-y-2" data-id="element-2202" data-mp="3hwv2xl1vxtau">
            {topicPrefs.map(topic => <li key={topic.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 px-5 py-4" data-id="element-2203" data-mp="33yf0w7eywqz1">
                <span className="min-w-0 flex-1" data-id="element-2204" data-mp="38zkue7ggci9m">
                  <span className="block font-bold text-ville-navy" data-id="element-2205" data-mp="2uz06wxzldy9s">{topic.label}</span>
                  <span className="block text-sm text-slate-500" data-id="element-2206" data-mp="2yztvzuortj49">{topic.description}</span>
                </span>
                <Toggle label={topic.label} defaultOn={topic.enabled} data-id="element-2207" data-mp="t6lrc5kit5wj" />
              </li>)}
          </ul>
        </section>

        <section aria-labelledby="places-title" className="mt-10" data-id="element-2208" data-mp="x7fir8x692u6">
          <h2 id="places-title" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-2209" data-mp="2hq9j2g0htd8u">
            My places
          </h2>
          <p className="mt-1 text-[15px] text-slate-600" data-id="element-2210" data-mp="2dqhai3b3n73h">
            Your places let the Town send you the notices that concern you.
          </p>
          <ul className="mt-4 space-y-2" data-id="element-2211" data-mp="2mpujyvz83vo7">
            {alertPlaces.map(place => <li key={place.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 px-5 py-4" data-id="element-2212" data-mp="2mojfsv1v8lwk">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" aria-hidden="true" data-id="element-2213" data-mp="31glck68n2ysd">
                  <MapPinIcon className="h-5 w-5" data-id="element-2214" data-mp="3aedrvaed26fr" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-2215" data-mp="35fu3z4g7ikkr">
                  <span className="block font-bold text-ville-navy" data-id="element-2216" data-mp="35h55syq3di98">{place.address}</span>
                  <span className="block text-sm text-slate-500" data-id="element-2217" data-mp="1g1xmscxawych">{place.label}</span>
                </span>
                <Toggle label={`Alerts for ${place.address}`} defaultOn data-id="element-2218" data-mp="1b45lwteoi1w5" />
              </li>)}
          </ul>
          <Link to="/my-profile" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline" data-id="element-2219" data-mp="3b6eymot00iis">
            Manage my addresses
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2220" data-mp="3l1p1tfv4uvy5" />
          </Link>
        </section>

        <section aria-labelledby="atna-settings-title" className="mt-10 rounded-3xl border border-slate-200 p-5" data-id="element-2221" data-mp="3p54u60066a51">
          <h2 id="atna-settings-title" className="flex items-center gap-2 text-[15px] font-extrabold text-ville-navy" data-id="element-2222" data-mp="3u2wthf358yj9">
            <SparklesIcon className="h-5 w-5 text-ville-indigo" aria-hidden="true" data-id="element-2223" data-mp="2usgpp8nynd2" />
            A question about your notices?
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600" data-id="element-2224" data-mp="cn7yalg1t9xf">
            ATNA can explain why you received a message, or how to change your preferences.
          </p>

          <form onSubmit={event => event.preventDefault()} className="mt-4 flex items-center gap-2 rounded-full border border-slate-200 py-1.5 pl-4 pr-1.5 focus-within:border-ville-indigo" data-id="element-2225" data-mp="lnd6gxt2gccn">
            <label htmlFor="atna-settings" className="sr-only" data-id="element-2226" data-mp="216kkl5ykzw6d">
              Ask your question
            </label>
            <input id="atna-settings" type="text" value={question} onChange={event => setQuestion(event.target.value)} placeholder="Ask ATNA…" className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-2227" data-mp="1x4h4g40yw3er" />
            <button type="submit" className="rounded-full bg-ville-indigo px-4 py-2 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-2228" data-mp="2oxznp0y6mhio">
              Ask
            </button>
          </form>

          <ul className="mt-3 flex flex-wrap gap-2" data-id="element-2229" data-mp="38ke4ooe9a9ra">
            {avisQuestions.map(item => <li key={item} data-id="element-2230" data-mp="33tperdi85ku9">
                <button type="button" onClick={() => setQuestion(item)} className="rounded-full bg-slate-50 px-3.5 py-1.5 text-sm text-slate-600 transition-colors duration-150 ease-ville hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2231" data-mp="33se91857y30i">
                  {item}
                </button>
              </li>)}
          </ul>
        </section>
      </div>
    </main>;
}